import { type FC, type PropsWithChildren, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import BusinessService from 'services/BusinessService';
import { toastVariant, useToast } from '__components/ToastProvider';
import { getErrorMessage } from 'hooks/useErrors';
import { getOriginalError } from 'services/AxiosService';
import { SubSectionWithHeader } from 'components/Layout/SubSection';
import { KizenTypography } from '__app/typography';
import {
  StyledDeleteButton,
  StyledModalBody,
  StyledNotice,
  StyledSettingsContentCard,
  StyledWarning,
} from './BusinessDeactivationStyled';
import { getChosenBusiness } from 'store/authentication/selectors';
import TextInput from '__components/Inputs/TextInput';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useAsync } from 'react-use';
import { getBusinessLicense, getSummaryUsage } from '@kizen/api/billing';
import AxiosService from 'services/AxiosService';
import Loader from '__components/Kizen/Loader';

const Warning: FC<PropsWithChildren> = ({ children }) => (
  <StyledNotice>
    <KizenTypography>{children}</KizenTypography>
  </StyledNotice>
);

const Break: FC = () => (
  <>
    <br />
    <br />
  </>
);

const DeleteConfirmationModal: FC<{
  onConfirm: () => Promise<void>;
  onHide: () => void;
}> = ({ onConfirm, onHide }) => {
  const { t } = useTranslation();
  const chosenBusiness = useSelector(getChosenBusiness);
  const {
    value: { renewalDate, standingAmount } = {
      renewalDate: undefined,
      standingAmount: undefined,
    },
    loading,
  } = useAsync(async () => {
    const [plans, usage] = await Promise.all([
      getBusinessLicense(AxiosService),
      getSummaryUsage(AxiosService),
    ]);

    const renewalDate = plans
      .filter((plan) => plan.price?.amount > 0 && plan.renewal_date)
      .map((plan) => new Date(plan.renewal_date))
      .sort((a, b) => b.getTime() - a.getTime())
      .pop();

    const standingAmount = [
      usage.emails,
      usage.automations,
      usage.core_platform as unknown as typeof usage.automations, // does not generate overages currently. It will in the near future, once we add AI billing capability
    ].reduce(
      (amount, item) =>
        amount +
        (item.currently_accrued_non_invoiced_overage_amount?.amount ?? 0),
      0
    );

    return {
      renewalDate:
        renewalDate &&
        new Date(
          Date.UTC(
            renewalDate.getUTCFullYear(),
            renewalDate.getUTCMonth(),
            renewalDate.getUTCDate() - 1
          )
        ).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        }),
      standingAmount:
        standingAmount > 0
          ? standingAmount.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })
          : undefined,
    };
  }, []);

  const [name, setName] = useState('');

  const confirmationName = t('CONFIRM DELETE');

  return (
    <BasicModal
      show
      size="medium"
      buttonText={standingAmount ? t('Close') : t('Delete this business')}
      leftBtn={standingAmount ? null : undefined}
      actionBtnColor={standingAmount ? 'blue' : 'red'}
      heading={t('Delete {{ businessName }}', {
        businessName: chosenBusiness.name,
      })}
      onConfirm={standingAmount ? onHide : onConfirm}
      onHide={onHide}
      disabled={(!standingAmount && name !== confirmationName) || loading}
    >
      <StyledModalBody>
        {standingAmount ? (
          <StyledWarning>
            {t(
              'Your business has an outstanding balance of {{ standingAmount }}. You can delete the business after generating and paying the invoice.',
              { standingAmount }
            )}
            <Break />
            {t(
              'Important: To avoid extra charges, please pause all active agentic workflows, marketing email broadcasts, and use of AI features before proceeding.'
            )}
          </StyledWarning>
        ) : (
          <Loader loading={loading} paddingTop={0}>
            <Warning>
              {t(
                'Deleting the business will also delete all associated data, including forms, agentic workflows, and uploaded files. Your data will be permanently deleted in accordance with our terms of service.'
              )}
              <Break />
              {t(
                'This action cannot be undone. If your organization is subject to compliance laws (such as HIPAA, GDPR, or CCPA), you may be legally required to download and retain a secure backup before proceeding.'
              )}
            </Warning>
            {renewalDate ? (
              <StyledWarning>
                {t('Your use of Kizen is paid through {{ renewalDate }}.', {
                  renewalDate,
                })}
              </StyledWarning>
            ) : null}
            <TextInput
              label={t(
                'To confirm deletion, type "{{ confirmationName }}" in the box below:',
                { confirmationName }
              )}
              placeholder={t('Type {{ confirmationName }} here', {
                confirmationName,
              })}
              value={name}
              onChange={setName}
            />
          </Loader>
        )}
      </StyledModalBody>
    </BasicModal>
  );
};

export const BusinessDeactivation = () => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleHide = () => {
    setShowConfirmation(false);
  };

  const handleConfirm = async () => {
    try {
      await BusinessService.deactivateBusiness();
      document.location.href = '/';
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to delete business.'),
        variant: toastVariant.FAILURE,
      });
    }
  };

  return (
    <>
      <StyledSettingsContentCard>
        <SubSectionWithHeader title={t('Delete Business')} action={null}>
          <StyledNotice>
            <KizenTypography>
              {t(
                'Delete this business and all associated data, including forms, agentic workflows, and uploaded files.'
              )}
            </KizenTypography>
          </StyledNotice>
          <StyledDeleteButton
            color="red"
            onClick={() => setShowConfirmation(true)}
          >
            {t('Delete this business')}
          </StyledDeleteButton>
        </SubSectionWithHeader>
      </StyledSettingsContentCard>
      {showConfirmation && (
        <DeleteConfirmationModal
          onConfirm={handleConfirm}
          onHide={handleHide}
        />
      )}
    </>
  );
};
